import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { DIRHAM_ICON_HEIGHT_EM, formatMoneyValue } from 'utils/currency';
import DirhamSymbol from './DirhamSymbol';

/** Use brand green when amount would render as default/black body text. */
function resolveAmountColor(color, sx = {}) {
  const fromSx = sx?.color;
  const effective = color ?? fromSx;

  if (
    effective == null ||
    effective === '' ||
    effective === 'inherit' ||
    effective === 'text.primary' ||
    effective === '#000' ||
    effective === '#000000' ||
    effective === 'rgba(0, 0, 0, 0.87)'
  ) {
    return 'primary.main';
  }

  return effective;
}

export default function CurrencyAmount({
  value,
  variant = 'body2',
  fontWeight,
  color,
  symbolSize,
  symbolScale = 1,
  align = 'flex-start',
  ...typographyProps
}) {
  const theme = useTheme();
  const { sx: typographySx, ...restTypography } = typographyProps;
  const formatted = formatMoneyValue(value);
  const isEmpty = formatted === '—';
  const typoKey = !variant || variant === 'inherit' ? 'body2' : variant;
  const typo = theme.typography[typoKey] || theme.typography.body2;

  const amountColor = resolveAmountColor(color, typographySx);

  const symbolSx =
    symbolSize != null
      ? undefined
      : {
          height: `${DIRHAM_ICON_HEIGHT_EM * symbolScale}em`,
          width: 'auto',
          maxWidth: `${DIRHAM_ICON_HEIGHT_EM * symbolScale * 1.15}em`,
          verticalAlign: '-0.06em'
        };

  return (
    <Stack
      direction="row"
      alignItems="baseline"
      spacing={0.35}
      component="span"
      sx={{
        display: 'inline-flex',
        fontSize: typo.fontSize,
        fontWeight: fontWeight ?? typo.fontWeight,
        lineHeight: 1.15,
        justifyContent: align === 'flex-end' ? 'flex-end' : undefined
      }}
    >
      {!isEmpty && <DirhamSymbol size={symbolSize} sx={symbolSx} />}
      <Typography
        component="span"
        color={amountColor}
        sx={{
          fontSize: 'inherit',
          fontWeight: 'inherit',
          lineHeight: 'inherit',
          ...typographySx
        }}
        {...restTypography}
      >
        {formatted}
      </Typography>
    </Stack>
  );
}

CurrencyAmount.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  variant: PropTypes.string,
  fontWeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  color: PropTypes.string,
  symbolSize: PropTypes.number,
  symbolScale: PropTypes.number,
  align: PropTypes.string
};
